import { useFormik } from 'formik';
import { useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { InferType, object, string } from 'yup';
import { toast } from 'react-toastify';
import { Input } from '../../../components/common/Form';
import ReservationOrderInputLabel from '../../../components/ReservationOrderInputLabel';
import { useRestaurantContext } from '../../../contexts/RestaurantContext';
import { updateURL } from '../../../api/restaurant';
import { openModal, useModalContext } from '../../../contexts/ModalContext';
import { handleHttpsInput } from '../../../utils/general';
import RestaurantWizardHeader from '../RestaurantWizard/RestaurantWizardHeader';
import LocationProfileActions from '../LocationProfile/LocationProfileActions';

const OnlineOrderingInputSchema = object({
  ordering: string().default('')
});

const OnlineOrderingPage = () => {
  const { restaurantID } = useParams<{ restaurantID: string }>();
  const { currentRestaurant, setRestaurant, updateRestaurant } = useRestaurantContext();
  const { dispatch } = useModalContext();

  useEffect(() => {
    if (restaurantID && currentRestaurant?.restaurantID !== Number(restaurantID)) {
      setRestaurant(Number(restaurantID));
    }
  }, [restaurantID, currentRestaurant?.restaurantID, setRestaurant]);

  const initialValues = useMemo(
    () => ({
      ordering: currentRestaurant?.orderingUrl || ''
    }),
    [currentRestaurant]
  );

  const formik = useFormik({
    initialValues,
    validationSchema: OnlineOrderingInputSchema,
    enableReinitialize: true,
    onSubmit: async (value: InferType<typeof OnlineOrderingInputSchema>) => {
      if (!currentRestaurant || value.ordering === (currentRestaurant.orderingUrl || '')) {
        return;
      }

      try {
        await updateURL(undefined, value.ordering);

        updateRestaurant({
          ...currentRestaurant,
          orderingUrl: value.ordering
        });

        toast.success('Successfully Updated!', {
          position: toast.POSITION.TOP_RIGHT
        });
      } catch (error) {
        openModal({
          dispatch,
          message:
            'An unexpected error has occurred. Please email our team at info@taptabapp.com so that we can address this issue promptly.'
        });
      }
    }
  });

  const handleInputChange = (value: string) => {
    formik.setFieldValue('ordering', handleHttpsInput(value));
  };

  return (
    <div className="restaurant-wizard-container reservation-order-container">
      <div className="restaurant-wizard-hero-background" aria-hidden="true" />

      <div className="restaurant-wizard-scroll-body">
        <RestaurantWizardHeader title="ONLINE ORDERING" />

        <form className="restaurant-wizard-content reservation-order-content" onSubmit={formik.handleSubmit}>
          <div className="reservation-order-page">
            <div className="reservation-order-inputs">
              <Input
                name="ordering"
                label={
                  <ReservationOrderInputLabel
                    label="online ordering"
                    text="Enter the link at which guests can make place online orders from your website."
                  />
                }
                className="reservation-order-input"
                onChange={(event: any) => handleInputChange(event.target.value)}
                onBlur={formik.handleBlur}
                value={formik.values.ordering}
                touched={formik.touched.ordering}
                error={formik.errors.ordering}
              />

              <p className="link-prompt online-ordering-note">
                <strong>IMPORTANT NOTE:</strong> If TapTab is your Online Ordering provider, then leave this field
                blank.
              </p>
            </div>

            <LocationProfileActions submit isSaving={formik.isSubmitting} />
          </div>
        </form>
      </div>
    </div>
  );
};

export default OnlineOrderingPage;
